import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

// Global variable to store the beforeinstallprompt event
let deferredPrompt: any = null;

// Global listener so we catch it early
window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event('pwa-prompt-ready'));
});

export const triggerPWAInstall = async () => {
    if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }
        deferredPrompt = null;
        window.dispatchEvent(new Event('pwa-prompt-ready')); // Update state
    } else {
        alert('De app kan momenteel niet automatisch geïnstalleerd worden via deze knop. Gebruik de "Zet op beginscherm" optie in het menu van je browser.');
    }
};

const PWAInstallPrompt: React.FC = () => {
    const [isPromptReady, setIsPromptReady] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isStandalone, setIsStandalone] = useState(true);

    useEffect(() => {
        // Check if device is mobile
        const checkMobile = () => window.innerWidth <= 768;
        setIsMobile(checkMobile());
        
        const handleResize = () => setIsMobile(checkMobile());
        window.addEventListener('resize', handleResize);

        // Check if already installed
        const isStandaloneCheck = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
        setIsStandalone(isStandaloneCheck);

        // Listen for the prompt
        const checkPrompt = () => setIsPromptReady(!!deferredPrompt);
        window.addEventListener('pwa-prompt-ready', checkPrompt);
        checkPrompt();
        
        // Listen for successful install
        window.addEventListener('appinstalled', () => {
            setIsStandalone(true);
            deferredPrompt = null;
        });

        // Check if user dismissed previously in localStorage
        const dismissedStr = localStorage.getItem('pwa-prompt-dismissed');
        if (dismissedStr) {
            // Dismiss for 7 days
            const dismissedDate = new Date(parseInt(dismissedStr));
            if (new Date().getTime() - dismissedDate.getTime() < 7 * 24 * 60 * 60 * 1000) {
                setIsDismissed(true);
            }
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('pwa-prompt-ready', checkPrompt);
        };
    }, []);

    const handleDismiss = () => {
        setIsDismissed(true);
        localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    };

    // Show prompt if: mobile AND not standalone AND not dismissed 
    // AND (prompt is ready OR it's an iOS device where auto-prompt isn't supported)
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const shouldShow = isMobile && !isStandalone && !isDismissed && (isPromptReady || isIos);

    if (!shouldShow) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[99999] p-4 bg-white/90 backdrop-blur-xl border-t border-slate-200 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] lg:hidden pb-safe animate-slideUp">
            <div className="flex items-start justify-between gap-4 max-w-md mx-auto">
                <div className="w-10 h-10 shrink-0 bg-[#e51b23] rounded-xl flex items-center justify-center text-white shadow-sm">
                    <Download size={20} />
                </div>
                
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-black text-slate-900 leading-tight">Installeer Werftools</h4>
                    <p className="text-xs text-slate-500 font-medium mt-0.5 mb-2 leading-relaxed">
                        {isIos 
                            ? 'Ervaar het klantportaal als native app. Tik op de Deel knop onderin en kies "Zet op beginscherm".'
                            : 'Ervaar het klantportaal sneller en schermvullend als native app op je telefoon.'}
                    </p>
                    
                    {!isIos && (
                        <button 
                            onClick={triggerPWAInstall}
                            className="bg-[#e51b23] text-white px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider w-full shadow-md active:scale-95 transition-transform"
                        >
                            Nu Installeren
                        </button>
                    )}
                </div>

                <button 
                    onClick={handleDismiss}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded-full transition-colors shrink-0"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
